import { type ChangeEvent, useCallback, useRef, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';

import { formatUnknownErrorMessage } from '../../constants/ErrorMessages';
import { useToast } from '../../hooks/useToast';
import { BaseModal } from '../BaseModal';
import { Button } from '../Button';
import { LazyImage } from '../LazyImage';
import {
  ButtonsContainer,
  ConfirmationDescription,
  ConfirmationTitle,
  Container,
  CropContainer,
  CropControls,
  CropModal,
  CropModalContent,
  DeleteButton,
  DeleteIcon,
  Fieldset,
  FileInputButton,
  FileInputContainer,
  FileInputText,
  HiddenInput,
  ImageContainer,
  Label,
  Loading,
  ZoomSlider,
} from './styles';

interface IUploadImageProps {
  label?: string;
  imageSrc?: string;
  onSelectImage: (file: File) => void;
  onRemoveImage?: () => void;
  onRemoveImagePreview: () => void;
  loadingDeleteImage?: boolean;
  loading?: boolean;
  allowCropControls?: boolean;
  cropAspect?: number;
  cropShape?: 'rect' | 'round';
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  originalFile: File
): Promise<File> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  const originalType = originalFile.type;
  const lastDotIndex = originalFile.name.lastIndexOf('.');
  const extension =
    lastDotIndex > 0 ? originalFile.name.slice(lastDotIndex + 1) : 'jpg';
  const fileName = `cropped-avatar.${extension}`;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], fileName, {
          type: originalType,
        });
        resolve(file);
      }
    }, originalType);
  });
};

export const UploadImageModal = ({
  label,
  imageSrc,
  onSelectImage,
  onRemoveImage,
  onRemoveImagePreview,
  loadingDeleteImage = false,
  loading = false,
  allowCropControls = false,
  cropAspect = 1,
  cropShape = 'round',
}: IUploadImageProps): JSX.Element => {
  const { addToast } = useToast();
  const [isModalConfirmationOpen, setIsModalConfirmationOpen] = useState(false);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [imageSrcPreview, setImageSrcPreview] = useState('');
  const [imageSrcPreviewCrop, setImageSrcPreviewCrop] = useState('');
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const [selectedFileName, setSelectedFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const removeIconImage = !!imageSrc || !!imageSrcPreview;

  const resetValues = useCallback(() => {
    setImageSrcPreview('');
    setImageSrcPreviewCrop('');
    setOriginalFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setSelectedFileName('');
    fileInputRef.current && (fileInputRef.current.value = '');
  }, []);

  const handleRemoveImagePreview = useCallback(() => {
    resetValues();
    onRemoveImagePreview();
  }, [onRemoveImagePreview, resetValues]);

  const handleOnChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      if (target.files?.length === 0) {
        handleRemoveImagePreview();
      }

      const newFile = target.files?.item(0);
      if (newFile) {
        setOriginalFile(newFile);
        setSelectedFileName(newFile.name);

        if (allowCropControls) {
          setImageSrcPreviewCrop(URL.createObjectURL(newFile));
          setIsCropModalOpen(true);
        } else {
          setImageSrcPreview(URL.createObjectURL(newFile));
          onSelectImage(newFile);
        }
      }
    },
    [allowCropControls, handleRemoveImagePreview, onSelectImage]
  );

  const handleFileButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDeleteClick = () => {
    if (imageSrcPreview) {
      handleRemoveImagePreview();
    } else {
      setIsModalConfirmationOpen(true);
    }
  };

  const handleCancelClick = () => {
    setIsModalConfirmationOpen(false);
  };

  const handleConfirmClick = () => {
    setIsModalConfirmationOpen(false);
    onRemoveImage?.();
  };

  const handleCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleCropConfirm = useCallback(async () => {
    if (imageSrcPreviewCrop && croppedAreaPixels && originalFile) {
      try {
        const croppedImage = await getCroppedImg(
          imageSrcPreviewCrop,
          croppedAreaPixels,
          originalFile
        );
        const croppedImageUrl = URL.createObjectURL(croppedImage);
        setImageSrcPreview(croppedImageUrl);
        onSelectImage(croppedImage);
        setIsCropModalOpen(false);
      } catch (error) {
        addToast({
          title: 'Error cropping image',
          description: formatUnknownErrorMessage(error),
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
      }
    }
  }, [
    imageSrcPreviewCrop,
    croppedAreaPixels,
    originalFile,
    onSelectImage,
    addToast,
  ]);

  const handleCropCancel = useCallback(() => {
    setIsCropModalOpen(false);
    resetValues();
  }, [resetValues]);

  return (
    <>
      <Container>
        {removeIconImage && (
          <DeleteButton
            data-cy="button-delete"
            type="button"
            onClick={handleDeleteClick}
            disabled={loadingDeleteImage}
          >
            <DeleteIcon />
          </DeleteButton>
        )}
        <ImageContainer
          data-cy="image-container"
          hasImage={!!imageSrcPreview || !!imageSrc}
          allowCropControls={allowCropControls}
          cropShape={cropShape}
        >
          {loadingDeleteImage ? (
            <Loading />
          ) : (
            <LazyImage
              src={imageSrcPreview || imageSrc}
              dataCy="image-profile"
            />
          )}
        </ImageContainer>
        <Fieldset hasLabel={!!label}>
          <FileInputContainer>
            <HiddenInput
              ref={fileInputRef}
              accept="image/*"
              onChange={handleOnChange}
              data-cy="input-file"
              type="file"
              disabled={loading}
            />
            <FileInputButton
              type="button"
              onClick={handleFileButtonClick}
              disabled={loading}
              data-cy="button-choose-file"
            >
              Choose File
            </FileInputButton>
            <FileInputText data-cy="file-name">
              {selectedFileName || 'No file chosen'}
            </FileInputText>
          </FileInputContainer>
          {label && <Label data-cy="label-profileImage">{label}</Label>}
        </Fieldset>

        <BaseModal
          open={isModalConfirmationOpen}
          onOpenChange={() => {
            setIsModalConfirmationOpen(false);
          }}
          zIndex={10}
        >
          <ConfirmationTitle data-cy="text-deleteImage">
            Delete Image
          </ConfirmationTitle>
          <ConfirmationDescription data-cy="description-deleteImage">
            Are you sure you want to delete the image?
          </ConfirmationDescription>

          <ButtonsContainer>
            <Button
              type="button"
              dataCy="button-cancel"
              styleType="outline"
              onClick={handleCancelClick}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              dataCy="button-submit"
              onClick={handleConfirmClick}
            >
              Confirm
            </Button>
          </ButtonsContainer>
        </BaseModal>
      </Container>

      {allowCropControls && (
        <CropModal
          open={isCropModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleCropCancel();
            }
          }}
          preventOutsideClose
          zIndex={15}
        >
          <CropModalContent>
            <ConfirmationTitle>Crop Image</ConfirmationTitle>

            <CropContainer>
              <Cropper
                image={imageSrcPreviewCrop}
                crop={crop}
                zoom={zoom}
                aspect={cropAspect}
                cropShape={cropShape}
                onCropChange={setCrop}
                onCropComplete={handleCropComplete}
                onZoomChange={setZoom}
              />
            </CropContainer>

            <CropControls>
              <Label>Zoom</Label>
              <ZoomSlider
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setZoom(Number(e.target.value))
                }
              />
            </CropControls>

            <ButtonsContainer>
              <Button
                type="button"
                dataCy="button-cropCancel"
                styleType="outline"
                onClick={handleCropCancel}
              >
                Cancel
              </Button>

              <Button
                type="button"
                dataCy="button-cropConfirm"
                onClick={() => void handleCropConfirm()}
              >
                Confirm
              </Button>
            </ButtonsContainer>
          </CropModalContent>
        </CropModal>
      )}
    </>
  );
};
